import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { fetchMyInvitations, respondToInvitation } from '../graphqlService';
import { formatErrorForUser } from '../utils/apolloError';
import { formatDate } from '../utils/format';
import { brand, neutral } from '../theme';
import Section from './ui/Section';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';

interface Invitation {
  id: string;
  email: string;
  name: string;
  status: 'invited' | 'pending';
  invitedBy: string;
  invitedAt: string;
  updatedAt: string;
}

interface InvitationsProps {
  /**
   * For pages where invitations are secondary (the editor's project list and
   * the creator dashboard): render nothing while loading or when there are
   * none, instead of a loading state and an empty panel.
   */
  hideWhenEmpty?: boolean;
  /** Shown above the invitations when there are any. */
  heading?: React.ReactNode;
  /**
   * Render the invitations as their own titled panel. Without a title they
   * are bare divider rows, for a page that already frames them in a Section.
   */
  title?: React.ReactNode;
  description?: React.ReactNode;
}

/** Invitation dates arrive as ISO strings or as millisecond timestamps. */
const invitedOn = (value?: string | null): string | null => {
  if (!value) return null;
  const text = String(value).trim();
  const date = new Date(/^\d+$/.test(text) ? Number(text) : text);
  return Number.isNaN(date.getTime()) ? null : formatDate(date);
};

/**
 * Invitations from creators to edit for them, as rows with Accept and
 * Decline. Each answer is confirmed first; accepting opens the projects list.
 */
const Invitations: React.FC<InvitationsProps> = ({ hideWhenEmpty = false, heading, title, description }) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: 'accept' | 'decline' | null }>({});
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    invitation: Invitation | null;
    action: 'accept' | 'decline' | null;
  }>({
    open: false,
    invitation: null,
    action: null,
  });

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const fetchedInvitations = await fetchMyInvitations();
      setInvitations(fetchedInvitations || []);
      setError('');
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError('We couldn’t load your invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleConfirmAction = (invitation: Invitation, action: 'accept' | 'decline') => {
    setConfirmDialog({
      open: true,
      invitation,
      action,
    });
  };

  const closeConfirm = () => setConfirmDialog({ open: false, invitation: null, action: null });

  const handleAction = async () => {
    const { invitation, action } = confirmDialog;
    if (!invitation || !action) return;

    try {
      setActionLoading(prev => ({ ...prev, [invitation.id]: action }));
      closeConfirm();

      // Use editorId-based accept/decline mutation (respondToInvitation)
      // This calls backend resolver that accepts by editor _id and requires authentication
      let result;
      if (action === 'accept') {
        result = await respondToInvitation(invitation.id, true);
      } else {
        result = await respondToInvitation(invitation.id, false);
      }

      // Backend returns the updated editor object; treat a returned object as success
      if (result && (result.id || result.status)) {
        // Remove the invitation from the list
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));

        // Redirect to edit-projects if accepted
        if (action === 'accept') {
          setTimeout(() => {
            window.location.href = '/edit-projects';
          }, 1500);
        }
      } else {
        // If backend returns structured error, result may contain message
        setError((result && (result.message || result.error)) || `The invitation could not be ${action === 'accept' ? 'accepted' : 'declined'}.`);
      }
    } catch (err: any) {
      console.error(`Error ${action}ing invitation:`, err);
      setError(formatErrorForUser(err));
    } finally {
      setActionLoading(prev => ({ ...prev, [invitation.id]: null }));
    }
  };

  const framed = Boolean(title);
  const frame = (content: React.ReactNode) =>
    framed ? (
      <Section title={title} description={description} disablePadding>
        {content}
      </Section>
    ) : (
      content
    );

  if (loading) {
    if (hideWhenEmpty) return null;
    return <>{frame(<LoadingState variant="list" count={1} label="Loading invitations" />)}</>;
  }

  if (error && invitations.length === 0) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadInvitations}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (invitations.length === 0) {
    if (hideWhenEmpty) return null;
    const empty = (
      <EmptyState
        compact
        icon={<MailOutlineOutlinedIcon />}
        title="No pending invitations"
        description="When a creator invites you to edit for them, the invitation appears here."
      />
    );
    return <>{framed ? frame(empty) : <Card>{empty}</Card>}</>;
  }

  const rows = (
    <Box component="ul" aria-label="Editor invitations" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {invitations.map((invitation, index) => {
        const busy = actionLoading[invitation.id];
        const when = invitedOn(invitation.invitedAt);
        return (
          <Box
            component="li"
            key={invitation.id}
            data-testid={`invitation-${invitation.id}`}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              px: framed ? { xs: 2, sm: 2.5 } : 0,
              py: 2,
              borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
              ...(framed ? {} : { '&:first-of-type': { pt: 0 }, '&:last-of-type': { pb: 0 } }),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: neutral[100],
                  border: `1px solid ${brand.border}`,
                  color: brand.slate,
                }}
              >
                <MailOutlineOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>Invitation to edit for a creator</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {[
                    `Sent to ${invitation.email}`,
                    when ? `Invited ${when}` : null,
                    invitation.status === 'pending' ? 'Registration pending' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, pl: { xs: 6, sm: 0 } }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={busy === 'accept' ? <CircularProgress size={14} color="inherit" /> : <CheckOutlinedIcon />}
                disabled={!!busy}
                onClick={() => handleConfirmAction(invitation, 'accept')}
              >
                Accept invitation
              </Button>
              <Button
                variant="text"
                color="inherit"
                size="small"
                startIcon={busy === 'decline' ? <CircularProgress size={14} color="inherit" /> : undefined}
                disabled={!!busy}
                onClick={() => handleConfirmAction(invitation, 'decline')}
              >
                Decline
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      {heading}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {frame(rows)}

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        aria-labelledby="confirm-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 700 }}>
          {confirmDialog.action === 'accept' ? 'Accept this invitation?' : 'Decline this invitation?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'accept'
              ? 'You’ll be able to work on this creator’s projects. Their projects appear in your list.'
              : 'The creator will be told you declined. They can invite you again later.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeConfirm} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            color={confirmDialog.action === 'accept' ? 'primary' : 'error'}
            variant="contained"
          >
            {confirmDialog.action === 'accept' ? 'Accept' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Invitations;
